/**
 * Notifications list and mutations – single source. List query stays **stale** (`staleTime: 0`) and refetches
 * whenever the notifications screen mounts or gains focus so new items show without waiting on cache TTL.
 * Mark-read mutations still use optimistic updates to limit flicker.
 */
import { useCallback, useMemo } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  type NotificationsListResult,
  rawNotificationIsRead,
} from './api';
import { useAuthStore } from '../../../shared/auth/store';
import type { Notification } from './apiTypes';
import { notificationsQueryKey } from './keys';

export type NotificationStub = Notification & { is_read?: boolean };

function patchReadFlags(n: Notification): Notification {
  return { ...n, read: true, is_read: true };
}

export type UseNotificationsOptions = {
  /**
   * When true, refetch whenever the calling screen is focused (use on the notifications inbox only).
   * Dashboard / badge callers should pass false so focus does not duplicate refetches; they still share the same cache.
   * @default true
   */
  refetchWhenNotificationsScreenFocused?: boolean;
};

export function useNotifications(options?: UseNotificationsOptions) {
  const refetch_when_notifications_screen_focused = options?.refetchWhenNotificationsScreenFocused ?? true;
  const customer_id = useAuthStore(s => s.user?.id ?? null);
  const enabled = typeof customer_id === 'number' && customer_id > 0;
  const query = useQuery({
    queryKey: notificationsQueryKey(customer_id),
    queryFn: fetchNotifications,
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  useFocusEffect(
    useCallback(() => {
      if (!refetch_when_notifications_screen_focused || !enabled) {
        return;
      }
      void query.refetch();
    }, [refetch_when_notifications_screen_focused, enabled, query.refetch]),
  );

  const data = useMemo(() => (query.data?.notifications ?? []) as NotificationStub[], [query.data]);
  const unread_count = query.data?.unread_count ?? 0;
  const error = useMemo(() => (query.error instanceof Error ? query.error : null), [query.error]);

  return useMemo(
    () => ({
      data,
      /** Server-provided unread count (`data.unread_count`); kept in sync with optimistic mark-read. */
      unread_count,
      /** True only on first load with no cached data — use for full-screen skeleton, not background refetch. */
      isPending: query.isPending,
      isFetching: query.isFetching,
      error,
      refetch: query.refetch,
    }),
    [data, unread_count, query.isPending, query.isFetching, error, query.refetch],
  );
}

export function useMarkAllAsRead() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onMutate: async () => {
      const key = notificationsQueryKey(useAuthStore.getState().user?.id ?? null);
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<NotificationsListResult>(key);
      client.setQueryData<NotificationsListResult>(key, old => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          notifications: old.notifications.map(n => patchReadFlags(n)),
          unread_count: 0,
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        const key = notificationsQueryKey(useAuthStore.getState().user?.id ?? null);
        client.setQueryData(key, context.previous);
      }
    },
  });
  return useMemo(
    () => ({
      mutate: mutation.mutate,
      mutateAsync: mutation.mutateAsync,
      isPending: mutation.isPending,
    }),
    [mutation.mutate, mutation.mutateAsync, mutation.isPending],
  );
}

export function useMarkAsRead() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (notification_id: number) => markNotificationAsRead(notification_id),
    onMutate: async (notification_id: number) => {
      const key = notificationsQueryKey(useAuthStore.getState().user?.id ?? null);
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<NotificationsListResult>(key);
      client.setQueryData<NotificationsListResult>(key, old => {
        if (!old) {
          return old;
        }
        let delta = 0;
        const notifications = old.notifications.map(n => {
          if (n.id !== notification_id) {
            return n;
          }
          const wasUnread = !rawNotificationIsRead({ is_read: n.is_read, read: n.read });
          if (wasUnread) {
            delta = 1;
          }
          return patchReadFlags(n);
        });
        return {
          ...old,
          notifications,
          unread_count: Math.max(0, old.unread_count - delta),
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        const key = notificationsQueryKey(useAuthStore.getState().user?.id ?? null);
        client.setQueryData(key, context.previous);
      }
    },
  });
  return useMemo(
    () => ({
      mutate: mutation.mutate,
      mutateAsync: mutation.mutateAsync,
      isPending: mutation.isPending,
    }),
    [mutation.mutate, mutation.mutateAsync, mutation.isPending],
  );
}
